import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";

// Default time threshold (15 minutes = 900000 milliseconds)
const DEFAULT_INACTIVITY_THRESHOLD = 15 * 60 * 1000;

export const useSessionTracker = (
    inactivityThreshold = DEFAULT_INACTIVITY_THRESHOLD
) => {
    const { firebaseUser } = useAuth();
    const [isNewSession, setIsNewSession] = useState(false);

    useEffect(() => {
        // Only track sessions for authenticated users
        if (!firebaseUser) {
            setIsNewSession(false);
            return;
        }

        // Get the current timestamp
        const currentTime = Date.now();

        // Get the last active timestamp from localStorage
        const lastActiveKey = `last_active_${firebaseUser.uid}`;
        const lastActiveTime = localStorage.getItem(lastActiveKey);

        if (!lastActiveTime) {
            // First login or localStorage was cleared
            setIsNewSession(true);
        } else {
            // Check if user was inactive longer than threshold
            const timeDiff = currentTime - parseInt(lastActiveTime, 10);
            setIsNewSession(timeDiff > inactivityThreshold);
        }

        // Update the last active time
        localStorage.setItem(lastActiveKey, currentTime.toString());

        // Set up interval to update the last active time periodically while user is active
        const interval = setInterval(() => {
            if (document.visibilityState === "visible") {
                localStorage.setItem(lastActiveKey, Date.now().toString());
            }
        }, 60000); // Update every minute

        // Set up visibility change listener
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                const storedTime = localStorage.getItem(lastActiveKey);
                if (storedTime) {
                    const timeDiff = Date.now() - parseInt(storedTime, 10);
                    if (timeDiff > inactivityThreshold) {
                        setIsNewSession(true);
                    }
                }
                localStorage.setItem(lastActiveKey, Date.now().toString());
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        // Clean up
        return () => {
            clearInterval(interval);
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
        };
    }, [firebaseUser, inactivityThreshold]);

    return { isNewSession };
};
